import { Controller } from "@hotwired/stimulus"

// Keeps a piece of the page current while a background job is still working on it - today the
// audio card being cut to 30s (#122), whose Audio::TrimJob finishes in a process the browser knows
// nothing about. Without this the band sits on "przycinamy" until it reloads the page by hand.
//
// A frame refetch rather than an ActionCable broadcast: the wait is seconds and only the owner of
// the profile watches it. Realtime chat is a separate, planned feature.
//
// The URL is ours to hand over rather than the frame's own `src`, because a frame carrying one
// would fetch it the moment the page loads - and get back the panel the server has just rendered
// into it. Nothing is fetched until there is a reason.
//
// The server decides when to stop: the `pending` target is rendered only while there is something
// to wait for, so a frame that comes back ready leaves the last timer to expire.
export default class extends Controller {
  static targets = ["pending"]
  static values = {
    url: String,
    interval: { type: Number, default: 3000 },
    // Two minutes. A trim is seconds of ffmpeg plus however long the queue takes to reach it;
    // past that the job has either failed (the panel says so) or nothing is running it.
    attempts: { type: Number, default: 40 },
    // Set where the frame is not the only thing on the page that changes with the card: on a
    // profile the "Listen"/"Posłuchaj" CTA is rendered outside it, and a frame navigation cannot reach it.
    // Off on the edit screen, where a reload would throw away a file picked but not saved.
    reloadPage: Boolean
  }

  connect() {
    this.attemptsLeft = this.attemptsValue
    this.#schedule()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.timer = null
  }

  #schedule() {
    this.timer = setTimeout(() => this.#poll(), this.intervalValue)
  }

  // A response with no frame in it (a 500, or an expired session redirecting to sign-in)
  // makes Turbo wipe the frame and put its own "Content missing" in its place. That would
  // take the panel - and with it the target this polling watches - off a page the band is
  // just standing on. Prevented, the frame keeps what it has and the next attempt tries
  // again; the request still rejects, which #poll logs.
  missing(event) {
    event.preventDefault()
  }

  // Checked here rather than before scheduling: by the time the timer fires, Stimulus has seen
  // the DOM the last reload brought in, so a card that is already ready costs no request at all.
  // The attempt budget is what ends a trim that never comes back.
  async #poll() {
    const frame = this.element.querySelector("turbo-frame")
    if (!frame || !this.urlValue || !this.hasPendingTarget || this.attemptsLeft <= 0) return

    this.attemptsLeft -= 1

    try {
      // Turbo fetches on the first src it is given and needs asking again after that.
      if (frame.src) {
        await frame.reload()
      } else {
        frame.src = this.urlValue
        await frame.loaded
      }
    } catch (error) {
      // A dropped request is the most ordinary thing that can happen to a phone on a train, and
      // giving up on the first one would leave the band watching a bar that never moves again.
      console.warn("[status-poll] could not refresh the panel", error)
    }

    // No `pending` target in what came back: the trim is over, one way or the other. If the rest
    // of the page has to catch up with it, this is the moment.
    if (!this.hasPendingTarget && this.reloadPageValue) {
      return window.Turbo.visit(window.location.href, { action: "replace" })
    }

    this.#schedule()
  }
}
